(() => {
  const VERSION = '2026.09.18.1';
  const confirmationLog = window.BHConfirmationLog || ((level, eventName, details = {}) => {
    const entry = { time: new Date().toISOString(), event: eventName, version: VERSION, ...details };
    window.__BH_CONFIRMATION_LOGS__ = window.__BH_CONFIRMATION_LOGS__ || [];
    window.__BH_CONFIRMATION_LOGS__.push(entry);
    if (window.__BH_CONFIRMATION_LOGS__.length > 150) window.__BH_CONFIRMATION_LOGS__.shift();
    const method = typeof console[level] === 'function' ? level : 'log';
    console[method]('[BH Confirmation]', eventName, entry);
    return entry;
  });
  window.BHConfirmationLog = confirmationLog;
  confirmationLog('info', 'asset:evaluated', {
    version: VERSION,
    alreadyDefined: Boolean(customElements.get('bh-cart-confirmation')),
    elementCount: document.querySelectorAll('bh-cart-confirmation').length
  });
  if (customElements.get('bh-cart-confirmation')) return;

  const responseCache = new Map();
  const normalize = value => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const isColorName = name => ['color', 'colour', 'tono'].some(token => normalize(name).includes(token));
  const isSizeName = name => ['talla', 'size', 'tamano', 'medida', 'dimension']
    .some(token => normalize(name).includes(token));
  const productText = product => normalize([
    product?.title,
    product?.type,
    ...(Array.isArray(product?.tags) ? product.tags : [])
  ].filter(Boolean).join(' '));
  const categories = {
    sheet_set: ['juego de sabana', 'set de sabana', 'set completo', 'juego de cama', 'sheet set'],
    fitted_sheet: ['sabana ajustable', 'sabana bajera', 'bajera ajustable', 'fitted sheet'],
    sheet: ['sabana', 'sabanas', 'sheet', 'sheets'],
    duvet_cover: ['funda nordica', 'duvet cover'],
    duvet: ['edredon', 'comforter', 'duvet', 'cobertor'],
    pillow: ['funda de almohada', 'almohada', 'pillowcase', 'pillow', 'cojin'],
    protector: ['protector', 'cubrecolchon', 'forro de colchon', 'forro colchon', 'antifluidos', 'topper'],
    blanket: ['manta', 'blanket', 'throw']
  };
  const complements = {
    fitted_sheet: ['duvet_cover', 'duvet', 'sheet_set', 'sheet', 'pillow', 'protector', 'blanket'],
    sheet: ['duvet_cover', 'duvet', 'fitted_sheet', 'sheet_set', 'pillow', 'protector', 'blanket'],
    sheet_set: ['duvet_cover', 'duvet', 'fitted_sheet', 'pillow', 'protector', 'blanket'],
    duvet_cover: ['duvet', 'sheet_set', 'sheet', 'fitted_sheet', 'pillow'],
    duvet: ['sheet_set', 'sheet', 'fitted_sheet', 'duvet_cover', 'pillow', 'protector'],
    pillow: ['sheet_set', 'sheet', 'fitted_sheet', 'duvet', 'duvet_cover', 'blanket'],
    protector: ['fitted_sheet', 'sheet_set', 'sheet', 'duvet', 'duvet_cover'],
    blanket: ['sheet_set', 'sheet', 'fitted_sheet', 'pillow', 'duvet_cover']
  };
  const categoryOf = value => {
    const text = normalize(value);
    return Object.keys(categories).find(key => categories[key].some(term => text.includes(term))) || '';
  };

  class BHCartConfirmation extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const first = document.querySelector('bh-cart-confirmation');
      if (first && first !== this) {
        confirmationLog('warn', 'component:duplicate-removed', { connected: this.isConnected });
        this.remove();
        return;
      }
      if (this.parentElement !== document.body) {
        confirmationLog('info', 'component:portal-to-body', {
          previousParent: this.parentElement?.tagName || null
        });
        document.body.append(this);
        return;
      }
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.dialog = this.querySelector('[data-bh-confirmation-dialog]');
      this.recommendations = this.querySelector('[data-bh-confirmation-recommendations]');
      this.grid = this.querySelector('[data-bh-confirmation-grid]');
      this.heading = this.querySelector('[data-bh-confirmation-heading]');
      this.titleElement = this.querySelector('.bh-cart-confirmation__title');
      if (this.dialog && this.dialog.parentElement !== document.body) {
        document.body.append(this.dialog);
      }
      this.skeletonMarkup = this.grid?.innerHTML || '';
      this.knownCartCount = Number(this.dataset.cartCount || 0);
      this.knownCartQuantities = new Map();
      confirmationLog('info', 'component:connected', {
        productId: this.dataset.productId || null,
        dialogFound: Boolean(this.dialog),
        gridFound: Boolean(this.grid),
        cartCount: this.knownCartCount,
        parent: this.parentElement?.tagName || null
      });
      this.dialog?.querySelector('[data-bh-confirmation-close]')?.addEventListener('click', () => this.close(), { signal });
      this.dialog?.addEventListener('cancel', event => {
        event.preventDefault();
      }, { signal });
      document.addEventListener('bh:cart-confirmation', event => {
        confirmationLog('info', 'event:bh-cart-confirmation', {
          source: event.detail?.source || null,
          variantId: event.detail?.variantId || event.detail?.item?.variant_id || null
        });
        this.open(event.detail || {});
      }, { signal });
      document.addEventListener('product:added', event => {
        if (event.detail?.source === 'bh-cart-confirmation') return;
        confirmationLog('info', 'event:product-added', {
          source: event.detail?.source || null,
          variantId: event.detail?.item?.variant_id || event.detail?.item?.id || null
        });
        this.openFromItem(event.detail?.item, event.detail || {});
      }, { signal });
      document.addEventListener('ajaxProduct:added', event => {
        confirmationLog('info', 'event:ajax-product-added', {
          variantId: event.detail?.item?.variant_id || event.detail?.variant_id || null
        });
        this.openFromItem(event.detail?.item || event.detail, event.detail || {});
      }, { signal });
      document.addEventListener('cart:updated', event => {
        confirmationLog('info', 'event:cart-updated', {
          source: event.detail?.source || null,
          itemCount: event.detail?.cart?.item_count ?? null
        });
        this.handleCartUpdated(event.detail || {});
      }, { signal });
      window.BHCartConfirmation = {
        element: this,
        open: detail => this.open(detail),
        close: () => this.close()
      };
      const warm = () => {
        if (/^\d+$/.test(this.dataset.productId || '')) this.fetchIntent(this.dataset.productId, 'complementary');
      };
      if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 1800 });
      else window.setTimeout(warm, 900);
      this.cartSnapshot().then(cart => {
        this.captureCart(cart);
        confirmationLog('info', 'cart:baseline-ready', { itemCount: cart?.item_count ?? null });
      }).catch(error => confirmationLog('warn', 'cart:baseline-error', { message: error?.message || String(error) }));
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      if (window.BHCartConfirmation?.element === this) delete window.BHCartConfirmation;
    }

    open(detail) {
      if (!this.isConnected) document.body.append(this);
      const liveDialog = document.querySelector('[data-bh-confirmation-dialog]');
      if (liveDialog) this.dialog = liveDialog;
      if (this.dialog && !this.dialog.isConnected) document.body.append(this.dialog);
      confirmationLog('info', 'dialog:open-called', {
        source: detail?.source || null,
        productId: detail?.productId || detail?.item?.product_id || null,
        variantId: detail?.variantId || detail?.item?.variant_id || detail?.item?.id || null,
        dialogFound: Boolean(this.dialog),
        dialogConnected: Boolean(this.dialog?.isConnected),
        dialogAlreadyOpen: Boolean(this.dialog?.open),
        connected: this.isConnected
      });
      if (!this.dialog) {
        confirmationLog('error', 'dialog:missing', {});
        return;
      }
      const eventKey = String(detail.variantId || detail.item?.variant_id || detail.item?.id || '');
      const now = Date.now();
      const isDuplicate = Boolean(
        eventKey &&
        this.lastOpenKey === eventKey &&
        now - (this.lastOpenAt || 0) < 1200
      );
      if (isDuplicate && this.dialog.open) {
        confirmationLog('info', 'dialog:duplicate-suppressed', { eventKey });
        this.scheduleOpenGuard(eventKey);
        return;
      }
      if (isDuplicate) {
        confirmationLog('warn', 'dialog:duplicate-reopen', { eventKey });
      }
      this.lastOpenKey = eventKey;
      this.lastOpenAt = now;
      this.returnFocus = document.activeElement;
      if (!isDuplicate) this.addedCount = Math.max(1, (this.addedCount || 0) + 1);
      this.updateAddedTitle();
      if (!this.dialog.open) {
        try {
          if (typeof this.dialog.showModal === 'function') {
            this.dialog.showModal();
            confirmationLog('info', 'dialog:show-modal-success', { open: this.dialog.open });
          } else {
            this.dialog.setAttribute('open', '');
            confirmationLog('warn', 'dialog:open-attribute-fallback', { open: this.dialog.open });
          }
        } catch (error) {
          this.dialog.setAttribute('open', '');
          this.dialog.classList.add('is-fallback-open');
          confirmationLog('error', 'dialog:show-modal-error', {
            message: error?.message || String(error),
            fallbackOpen: this.dialog.hasAttribute('open')
          });
        }
      }
      confirmationLog('info', 'dialog:open-state', {
        open: this.dialog.open,
        hasOpenAttribute: this.dialog.hasAttribute('open'),
        fallback: this.dialog.classList.contains('is-fallback-open')
      });
      this.scheduleOpenGuard(eventKey);
      this.loadRecommendations(detail);
    }

    scheduleOpenGuard(eventKey) {
      window.clearTimeout(this.openGuardTimer);
      const guardVersion = (this.openGuardVersion || 0) + 1;
      this.openGuardVersion = guardVersion;
      this.openGuardTimer = window.setTimeout(() => {
        if (this.dialog && !this.dialog.isConnected) document.body.append(this.dialog);
        if (
          guardVersion !== this.openGuardVersion ||
          !this.isConnected ||
          !this.dialog ||
          this.dialog.open
        ) return;
        try {
          if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
          else this.dialog.setAttribute('open', '');
          confirmationLog('warn', 'dialog:guard-reopened', {
            version: VERSION,
            eventKey,
            open: Boolean(this.dialog.open),
            hasOpenAttribute: this.dialog.hasAttribute('open')
          });
        } catch (error) {
          this.dialog.setAttribute('open', '');
          this.dialog.classList.add('is-fallback-open');
          confirmationLog('error', 'dialog:guard-fallback', {
            version: VERSION,
            eventKey,
            message: error?.message || String(error),
            hasOpenAttribute: this.dialog.hasAttribute('open')
          });
        }
      }, 180);
    }

    openFromItem(item, detail = {}) {
      if (!item) return;
      this.open({
        ...detail,
        item,
        productId: String(item?.product_id || detail.productId || ''),
        variantId: String(item?.variant_id || item?.id || detail.variantId || ''),
        productTitle: String(item?.product_title || item?.title || detail.productTitle || ''),
        color: String(detail.color || this.sourceColor(item)),
        source: detail.source || 'cart-event'
      });
    }

    captureCart(cart) {
      if (!cart) return;
      this.knownCartCount = Number(cart.item_count || 0);
      this.knownCartQuantities = new Map((cart.items || []).map(item => [
        String(item?.variant_id || item?.id || ''),
        Number(item?.quantity || 0)
      ]));
    }

    handleCartUpdated(detail) {
      const cart = detail?.cart;
      if (!cart) return;
      const previousCount = Number(this.knownCartCount || 0);
      const addedItem = (cart.items || []).find(item => {
        const id = String(item?.variant_id || item?.id || '');
        return Number(item?.quantity || 0) > Number(this.knownCartQuantities?.get(id) || 0);
      }) || cart.items?.[0];
      const increased = Number(cart.item_count || 0) > previousCount;
      confirmationLog('info', 'cart:update-evaluated', {
        source: detail.source || null,
        previousCount,
        nextCount: Number(cart.item_count || 0),
        increased,
        suppressConfirmation: Boolean(detail.suppressConfirmation),
        addedVariantId: addedItem?.variant_id || addedItem?.id || null
      });
      this.captureCart(cart);
      if (
        !increased ||
        detail.suppressConfirmation ||
        detail.source === 'bh-cart-confirmation'
      ) {
        if (increased && detail.suppressConfirmation) {
          confirmationLog('info', 'cart:update-open-deferred', {
            source: detail.source || null,
            addedVariantId: addedItem?.variant_id || addedItem?.id || null
          });
        }
        return;
      }
      this.openFromItem(addedItem, { ...detail, source: detail.source || 'cart:updated' });
    }

    updateAddedTitle() {
      if (!this.titleElement) return;
      this.titleElement.textContent = this.addedCount > 1
        ? `${this.addedCount} ${this.dataset.itemsAddedLabel}`
        : this.dataset.itemAddedLabel;
    }

    close(restoreFocus = true) {
      this.openGuardVersion = (this.openGuardVersion || 0) + 1;
      window.clearTimeout(this.openGuardTimer);
      this.requestVersion = (this.requestVersion || 0) + 1;
      this.addedCount = 0;
      if (this.dialog?.open && typeof this.dialog.close === 'function') this.dialog.close();
      else this.dialog?.removeAttribute('open');
      this.dialog?.classList.remove('is-fallback-open');
      if (restoreFocus && this.returnFocus instanceof HTMLElement && this.returnFocus.isConnected) {
        this.returnFocus.focus({ preventScroll: true });
      }
    }

    sourceColor(item) {
      const option = item?.options_with_values?.find(entry => isColorName(entry?.name));
      return String(option?.value || '');
    }

    sourceSize(item) {
      const option = item?.options_with_values?.find(entry => isSizeName(entry?.name));
      return String(option?.value || '');
    }

    serverDecisionEndpoint(productId, variantId, cart) {
      const endpoint = new URL(this.dataset.serverRecommendationsUrl || '/apps/bambu-analytics?mode=recommendations', window.location.origin);
      const items = Array.isArray(cart?.items) ? cart.items.slice(0, 30) : [];
      endpoint.searchParams.set('mode', 'recommendations');
      endpoint.searchParams.set('product_id', productId);
      endpoint.searchParams.set('variant_id', variantId);
      endpoint.searchParams.set('cart_product_ids', items.map(item => item?.product_id || '').join(','));
      endpoint.searchParams.set('cart_variant_ids', items.map(item => item?.variant_id || item?.id || '').join(','));
      endpoint.searchParams.set('cart_quantities', items.map(item => item?.quantity || 0).join(','));
      endpoint.searchParams.set('cart_categories', items.map(item => categoryOf(this.cartItemText(item))).filter(Boolean).join(','));
      return endpoint.href;
    }

    async fetchServerDecision(productId, variantId, cart) {
      if (!/^\d+$/.test(variantId)) return null;
      const response = await fetch(this.serverDecisionEndpoint(productId, variantId, cart), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`Recommendation decision failed: ${response.status}`);
      const decision = await response.json();
      return ['PROMOTION_VARIANTS', 'CROSS_SELL'].includes(decision?.strategy) ? decision : null;
    }

    promotionVariantCards(product, decision, detail, cart) {
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      const options = this.optionObjects(product);
      const colorIndex = options.findIndex(option => isColorName(option.name));
      const sizeIndex = options.findIndex(option => isSizeName(option.name));
      if (colorIndex < 0 || variants.length < 2) return [];
      const currentVariantId = String(detail.variantId || detail.item?.variant_id || detail.item?.id || '');
      const sourceVariant = variants.find(variant => String(variant?.id || '') === currentVariantId);
      const sourceValues = Array.isArray(sourceVariant?.options)
        ? sourceVariant.options
        : [sourceVariant?.option1, sourceVariant?.option2, sourceVariant?.option3];
      const sourceSize = this.sourceSize(detail.item) || String(sourceValues[sizeIndex] || '');
      const sourceColor = this.sourceColor(detail.item) || String(sourceValues[colorIndex] || '');
      const eligible = new Set((decision?.eligibleVariantIds || []).map(String));
      const inCart = new Set((cart?.items || []).map(item => String(item?.variant_id || item?.id || '')));
      const byColor = new Map();
      variants.forEach(variant => {
        const id = String(variant?.id || '');
        if (!id || id === currentVariantId || inCart.has(id) || variant?.available === false) return;
        if (eligible.size && !eligible.has(id)) return;
        const values = Array.isArray(variant?.options)
          ? variant.options
          : [variant?.option1, variant?.option2, variant?.option3];
        const color = String(values[colorIndex] || '');
        const size = sizeIndex >= 0 ? String(values[sizeIndex] || '') : '';
        if (!color || normalize(color) === normalize(sourceColor)) return;
        const score = (sourceSize && normalize(size) === normalize(sourceSize) ? 1000 : 0) + (variant?.featured_image ? 50 : 0);
        const existing = byColor.get(normalize(color));
        if (!existing || score > existing.score) {
          byColor.set(normalize(color), {
            product,
            variant,
            colorMatched: false,
            recommendationLabel: sourceSize ? `${color} · ${sourceSize}` : color,
            directAdd: true,
            score
          });
        }
      });
      return [...byColor.values()]
        .sort((left, right) => right.score - left.score)
        .slice(0, Number(decision?.limit || 4));
    }

    selectVariant(product, sourceColor) {
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      const available = variants.filter(variant => variant?.available !== false);
      if (!available.length) {
        if (product?.available === false) return null;
        return { variant: { id: null, price: product?.price, featured_image: product?.featured_image }, colorMatched: false };
      }
      const colorIndex = Array.isArray(product?.options)
        ? product.options.findIndex(option => isColorName(typeof option === 'string' ? option : option?.name))
        : -1;
      if (sourceColor && colorIndex >= 0) {
        const exact = available.find(candidate => {
          const values = Array.isArray(candidate?.options)
            ? candidate.options
            : [candidate?.option1, candidate?.option2, candidate?.option3];
          return normalize(values[colorIndex]) === normalize(sourceColor);
        });
        if (exact) return { variant: exact, colorMatched: true };
      }
      return { variant: available[0], colorMatched: false };
    }

    recommendationEndpoint(productId, intent) {
      const root = window.Shopify?.routes?.root || '/';
      let source = this.dataset.recommendationsUrl || `${root}recommendations/products.json`;
      if (!/\.json(?:\?|$)/.test(source)) source = source.replace(/\/?$/, '.json');
      const endpoint = new URL(source, window.location.origin);
      endpoint.searchParams.set('product_id', productId);
      endpoint.searchParams.set('limit', '10');
      endpoint.searchParams.set('intent', intent);
      return endpoint.href;
    }

    fetchJson(url) {
      if (!responseCache.has(url)) {
        responseCache.set(url, fetch(url, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        }).then(response => {
          if (!response.ok) throw new Error(`Request failed: ${response.status}`);
          return response.json();
        }).catch(error => {
          responseCache.delete(url);
          throw error;
        }));
      }
      return responseCache.get(url);
    }

    async fetchIntent(productId, intent) {
      const payload = await this.fetchJson(this.recommendationEndpoint(productId, intent));
      return Array.isArray(payload?.products) ? payload.products : [];
    }

    searchTerms(sourceCategory, targetOrder) {
      const targets = targetOrder?.length
        ? targetOrder
        : complements[sourceCategory] || ['duvet_cover', 'fitted_sheet', 'sheet_set', 'pillow', 'protector'];
      const terms = targets.slice(0, 4).flatMap(category => (
        category === 'fitted_sheet'
          ? ['sabana ajustable', 'sabana bajera']
          : [categories[category][0]]
      ));
      return [...new Set(terms)].slice(0, 6);
    }

    productJsonUrl(product) {
      const root = window.Shopify?.routes?.root || '/';
      const source = product?.url || (product?.handle ? `${root}products/${product.handle}` : '');
      if (!source) return '';
      const endpoint = new URL(source, window.location.origin);
      endpoint.search = '';
      endpoint.hash = '';
      endpoint.pathname = `${endpoint.pathname.replace(/\.js$/, '').replace(/\/$/, '')}.js`;
      return endpoint.href;
    }

    async hydrateProduct(product) {
      const url = this.productJsonUrl(product);
      if (!url) return product;
      try {
        const full = await this.fetchJson(url);
        return {
          ...product,
          ...full,
          url: product.url || full.url || `/products/${full.handle || product.handle}`
        };
      } catch (_) {
        return product;
      }
    }

    needsHydration(product, sourceColor) {
      if (!product?.handle && !product?.url) return false;
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      const options = Array.isArray(product?.options) ? product.options : [];
      if (!variants.length || !options.length) return true;
      const selected = this.selectVariant(product, sourceColor);
      if (!selected?.variant?.id || selected.variant.price == null) return true;
      return Boolean(sourceColor) && !selected.colorMatched;
    }

    async prepareProducts(products, sourceColor, sourceCategory, targetOrder) {
      const unique = new Map();
      products.forEach(product => {
        const id = String(product?.id || product?.handle || '');
        if (!id || unique.has(id)) return;
        if (sourceCategory && this.relationshipScore(sourceCategory, product, targetOrder) <= 0) return;
        unique.set(id, product);
      });
      const candidates = [...unique.values()].slice(0, 12);
      const hydrated = await Promise.allSettled(candidates.map(product => (
        this.needsHydration(product, sourceColor) ? this.hydrateProduct(product) : product
      )));
      return hydrated.map((result, index) => result.status === 'fulfilled' ? result.value : candidates[index]);
    }

    async searchCatalog(sourceCategory, targetOrder) {
      const root = window.Shopify?.routes?.root || '/';
      const source = this.dataset.searchUrl || `${root}search/suggest.json`;
      const requests = this.searchTerms(sourceCategory, targetOrder).map(term => {
        const endpoint = new URL(source, window.location.origin);
        endpoint.searchParams.set('q', term);
        endpoint.searchParams.set('resources[type]', 'product');
        endpoint.searchParams.set('resources[limit]', '6');
        endpoint.searchParams.set('resources[options][unavailable_products]', 'hide');
        return this.fetchJson(endpoint.href).then(payload => payload?.resources?.results?.products || []);
      });
      const settled = await Promise.allSettled(requests);
      const unique = new Map();
      settled.flatMap(result => result.status === 'fulfilled' && Array.isArray(result.value) ? result.value : [])
        .forEach(product => {
          const id = String(product?.id || product?.handle || '');
          if (id && !unique.has(id) && this.relationshipScore(sourceCategory, product, targetOrder) > 0) unique.set(id, product);
        });
      const ranked = [...unique.values()]
        .sort((left, right) => this.relationshipScore(sourceCategory, right, targetOrder) - this.relationshipScore(sourceCategory, left, targetOrder));
      const candidates = [];
      const selectedIds = new Set();
      (targetOrder?.length ? targetOrder : complements[sourceCategory] || []).forEach(targetCategory => {
        const match = ranked.find(product => (
          categoryOf(productText(product)) === targetCategory &&
          !selectedIds.has(String(product?.id || product?.handle || ''))
        ));
        if (!match) return;
        candidates.push(match);
        selectedIds.add(String(match?.id || match?.handle || ''));
      });
      ranked.forEach(product => {
        const id = String(product?.id || product?.handle || '');
        if (candidates.length < 12 && !selectedIds.has(id)) {
          candidates.push(product);
          selectedIds.add(id);
        }
      });
      const hydrated = await Promise.allSettled(candidates.map(product => this.hydrateProduct(product)));
      return hydrated.map((result, index) => result.status === 'fulfilled' ? result.value : candidates[index]);
    }

    relationshipScore(sourceCategory, product, targetOrder) {
      const target = categoryOf(productText(product));
      const order = targetOrder?.length ? targetOrder : complements[sourceCategory] || [];
      const index = order.indexOf(target);
      return index < 0 ? 0 : 300 - (index * 30);
    }

    buildCards(productId, color, sourceCategory, curated, related, searched, options = {}) {
      const unique = new Map();
      const excludedIds = options.excludedIds || new Set();
      const targetOrder = options.targetOrder || [];
      const add = (products, origin) => products.forEach(product => {
        const id = String(product?.id || '');
        if (!id || id === productId || excludedIds.has(id) || product?.available === false || unique.has(id)) return;
        const selected = this.selectVariant(product, color);
        if (!selected) return;
        const relationship = this.relationshipScore(sourceCategory, product, targetOrder);
        if (sourceCategory && relationship <= 0) return;
        unique.set(id, {
          product,
          ...selected,
          origin,
          relationship
        });
      });
      add(curated, 'curated');
      add(related, 'related');
      add(searched, 'search');
      const ranked = [...unique.values()].sort((left, right) => {
        const leftScore = (left.origin === 'curated' ? 1000 : 0) + left.relationship + (left.colorMatched ? 80 : 0);
        const rightScore = (right.origin === 'curated' ? 1000 : 0) + right.relationship + (right.colorMatched ? 80 : 0);
        return rightScore - leftScore;
      });
      const cards = [];
      const usedCategories = new Set();
      ranked.forEach(card => {
        const category = categoryOf(productText(card.product));
        if (cards.length >= 4 || !category || usedCategories.has(category)) return;
        cards.push(card);
        usedCategories.add(category);
      });
      ranked.forEach(card => {
        if (cards.length < 4 && !cards.includes(card)) cards.push(card);
      });
      if ((targetOrder.length ? targetOrder : complements[sourceCategory] || []).includes('fitted_sheet')) {
        const fittedSheet = ranked.find(card => categoryOf(productText(card.product)) === 'fitted_sheet');
        if (fittedSheet && !cards.includes(fittedSheet)) {
          if (cards.length >= 4) cards[cards.length - 1] = fittedSheet;
          else cards.push(fittedSheet);
        }
      }
      return cards.slice(0, 4);
    }

    imageSources(product, variant) {
      const sources = [];
      const add = value => {
        if (!value) return;
        if (typeof value === 'string') {
          const clean = value.trim();
          if (clean && clean !== '[object Object]' && !sources.includes(clean)) sources.push(clean);
          return;
        }
        if (typeof value !== 'object') return;
        ['src', 'url', 'original_src', 'image_url'].forEach(key => add(value[key]));
        add(value.preview_image);
        add(value.image);
      };
      add(variant?.featured_image);
      add(variant?.image);
      add(product?.featured_image);
      add(product?.featured_media?.preview_image);
      add(product?.image);
      if (Array.isArray(product?.images)) product.images.forEach(add);
      return sources.map(source => this.resizeImage(source));
    }

    cartItemText(item) {
      return [
        item?.product_title,
        item?.title,
        item?.product_type,
        item?.variant_title,
        ...(Array.isArray(item?.options_with_values)
          ? item.options_with_values.map(option => `${option?.name || ''} ${option?.value || ''}`)
          : [])
      ].filter(Boolean).join(' ');
    }

    journeyFor(cart, sourceCategory) {
      const cartCategories = new Set((cart?.items || []).map(item => categoryOf(this.cartItemText(item))).filter(Boolean));
      const hasSheetBase = ['sheet_set', 'fitted_sheet', 'sheet'].some(category => cartCategories.has(category));
      const hasWarmLayer = ['duvet', 'duvet_cover'].some(category => cartCategories.has(category));
      if (!hasSheetBase && (hasWarmLayer || ['duvet', 'duvet_cover'].includes(sourceCategory))) {
        return {
          heading: this.dataset.comboHeading,
          targets: ['sheet_set', 'fitted_sheet', 'sheet', 'pillow', 'protector']
        };
      }
      if (hasSheetBase && !hasWarmLayer) {
        return {
          heading: this.dataset.comboHeading,
          targets: ['duvet', 'duvet_cover', 'pillow', 'protector', 'blanket']
        };
      }
      if ((hasSheetBase || hasWarmLayer) && !cartCategories.has('protector')) {
        return {
          heading: this.dataset.protectHeading,
          targets: ['protector', 'pillow', 'blanket', 'fitted_sheet', 'duvet_cover']
        };
      }
      return {
        heading: this.dataset.finishHeading,
        targets: ['pillow', 'blanket', 'duvet_cover', 'protector', 'fitted_sheet', 'sheet_set']
      };
    }

    async cartSnapshot() {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      return response.json();
    }

    async loadRecommendations(detail) {
      const version = (this.requestVersion || 0) + 1;
      this.requestVersion = version;
      const productId = String(detail.productId || detail.item?.product_id || this.dataset.productId || '');
      const variantId = String(detail.variantId || detail.item?.variant_id || detail.item?.id || '');
      const color = String(detail.color || this.sourceColor(detail.item));
      const sourceContext = String(detail.productTitle || detail.item?.product_title || this.dataset.productContext || '');
      const sourceCategory = categoryOf(sourceContext);
      confirmationLog('info', 'recommendations:start', {
        version,
        productId,
        color: color || null,
        sourceCategory: sourceCategory || null,
        sourceContext
      });
      this.grid.innerHTML = this.skeletonMarkup;
      this.recommendations.hidden = false;
      this.recommendations.setAttribute('aria-busy', 'true');
      if (!/^\d+$/.test(productId)) {
        confirmationLog('error', 'recommendations:invalid-product-id', { productId });
        this.recommendations.hidden = true;
        this.recommendations.setAttribute('aria-busy', 'false');
        return;
      }
      try {
        const cart = detail.cart || await this.cartSnapshot().catch(() => null);
        const decision = await this.fetchServerDecision(productId, variantId, cart).catch(error => {
          confirmationLog('warn', 'recommendations:server-fallback', {
            productId,
            variantId,
            message: error?.message || String(error)
          });
          return null;
        });
        if (decision?.strategy === 'PROMOTION_VARIANTS') {
          const productUrl = detail.item?.url || (productId === String(this.dataset.productId || '') ? window.location.pathname : '');
          const sourceProduct = await this.hydrateProduct({ id: productId, url: productUrl });
          const promotionCards = this.promotionVariantCards(sourceProduct, decision, detail, cart);
          if (promotionCards.length) {
            if (version !== this.requestVersion) return;
            if (this.heading) this.heading.textContent = decision.heading;
            this.grid.replaceChildren();
            promotionCards.forEach((card, index) => {
              const element = this.createCard(card, color);
              element.style.setProperty('--bh-card-index', String(index));
              this.grid.append(element);
            });
            this.recommendations.hidden = false;
            confirmationLog('info', 'recommendations:promotion-variants-rendered', {
              productId,
              promotionId: decision.promotion?.id || null,
              eligibleQuantity: decision.promotion?.eligibleQuantity ?? null,
              remainingQuantity: decision.promotion?.remainingQuantity ?? null,
              variantIds: promotionCards.map(card => String(card.variant?.id || ''))
            });
            return;
          }
        }
        const [curatedResult, relatedResult] = await Promise.allSettled([
          this.fetchIntent(productId, 'complementary'),
          this.fetchIntent(productId, 'related')
        ]);
        const rawCurated = curatedResult.status === 'fulfilled' ? curatedResult.value : [];
        const rawRelated = relatedResult.status === 'fulfilled' ? relatedResult.value : [];
        const fallbackJourney = this.journeyFor(cart, sourceCategory);
        const journey = decision?.strategy === 'CROSS_SELL'
          ? { heading: decision.heading, targets: decision.targetCategories }
          : fallbackJourney;
        const excludedIds = new Set((cart?.items || []).map(item => String(item?.product_id || '')).filter(Boolean));
        if (this.heading) this.heading.textContent = journey.heading;
        const [curated, related] = await Promise.all([
          this.prepareProducts(rawCurated, color, sourceCategory, journey.targets),
          this.prepareProducts(rawRelated, color, sourceCategory, journey.targets)
        ]);
        const cardOptions = { excludedIds, targetOrder: journey.targets };
        let cards = this.buildCards(productId, color, sourceCategory, curated, related, [], cardOptions);
        const needsFittedSheet = journey.targets.includes('fitted_sheet') &&
          !cards.some(card => categoryOf(productText(card.product)) === 'fitted_sheet');
        if (cards.length < 4 || needsFittedSheet) {
          const searched = await this.searchCatalog(sourceCategory, journey.targets).catch(() => []);
          cards = this.buildCards(productId, color, sourceCategory, curated, related, searched, cardOptions);
        }
        if (version !== this.requestVersion) return;
        this.grid.replaceChildren();
        cards.forEach((card, index) => {
          const element = this.createCard(card, color);
          element.style.setProperty('--bh-card-index', String(index));
          this.grid.append(element);
        });
        this.recommendations.hidden = cards.length === 0;
        confirmationLog(cards.length ? 'info' : 'warn', 'recommendations:rendered', {
          version,
          productId,
          heading: journey.heading,
          cardCount: cards.length,
          productIds: cards.map(card => String(card.product?.id || ''))
        });
      } catch (error) {
        confirmationLog('error', 'recommendations:error', {
          version,
          productId,
          message: error?.message || String(error)
        });
        if (version === this.requestVersion) this.recommendations.hidden = true;
      } finally {
        if (version === this.requestVersion) this.recommendations.setAttribute('aria-busy', 'false');
      }
    }

    createCard({ product, variant, colorMatched, recommendationLabel, directAdd }, sourceColor) {
      const card = document.createElement('article');
      card.className = 'bh-cart-confirmation__card';
      const link = document.createElement('a');
      const productUrl = new URL(product.url, window.location.origin);
      if (variant.id) productUrl.searchParams.set('variant', String(variant.id));
      link.className = 'bh-cart-confirmation__link';
      link.href = productUrl.href;
      const media = document.createElement('span');
      media.className = 'bh-cart-confirmation__media';
      const priceValue = Number(variant.price ?? product.price ?? 0);
      const compareValue = Number(variant.compare_at_price ?? product.compare_at_price ?? 0);
      if (compareValue > priceValue) {
        const badge = document.createElement('span');
        badge.className = 'bh-cart-confirmation__badge';
        badge.textContent = this.dataset.offerLabel;
        media.append(badge);
      }
      const imageSources = this.imageSources(product, variant);
      if (imageSources.length) {
        const image = document.createElement('img');
        let imageIndex = 0;
        image.src = imageSources[imageIndex];
        image.alt = product.title || '';
        image.loading = 'lazy';
        image.decoding = 'async';
        image.width = 360;
        image.height = 450;
        image.addEventListener('error', () => {
          imageIndex += 1;
          if (imageSources[imageIndex]) image.src = imageSources[imageIndex];
          else image.remove();
        });
        media.append(image);
      }
      link.append(media);
      const title = document.createElement('span');
      title.className = 'bh-cart-confirmation__product-title';
      title.textContent = product.title || '';
      link.append(title);
      if (recommendationLabel || (colorMatched && sourceColor)) {
        const color = document.createElement('span');
        color.className = 'bh-cart-confirmation__color';
        color.textContent = recommendationLabel || `${this.dataset.sameColorLabel}: ${sourceColor}`;
        link.append(color);
      }
      const price = document.createElement('span');
      price.className = 'bh-cart-confirmation__price';
      price.textContent = this.money(priceValue);
      link.append(price);
      if (compareValue > priceValue) {
        const compare = document.createElement('s');
        compare.className = 'bh-cart-confirmation__compare-price';
        compare.textContent = this.money(compareValue);
        link.append(compare);
      }
      card.append(link);

      const action = document.createElement('button');
      const requiresSelection = !directAdd && this.hasSelectionOptions(product);
      action.type = 'button';
      action.className = 'bh-cart-confirmation__action';
      action.innerHTML = `${this.bagIcon()}<span>${requiresSelection ? this.dataset.selectSizeLabel : this.dataset.addLabel}</span>`;
      action.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        if (requiresSelection) this.openOptionsModal(product, variant, productUrl.href);
        else this.addVariant(product, variant, sourceColor, action);
      });
      card.append(action);
      return card;
    }

    optionObjects(product) {
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      return (Array.isArray(product?.options) ? product.options : []).map((option, index) => {
        const name = typeof option === 'string' ? option : option?.name;
        const supplied = typeof option === 'object' && Array.isArray(option?.values) ? option.values : [];
        const values = supplied.length ? supplied : variants.map(variant => variant?.options?.[index]).filter(Boolean);
        return {
          name: String(name || `Option ${index + 1}`),
          values: [...new Map(values.map(value => {
            const text = String(value?.name || value || '');
            return [normalize(text), text];
          })).values()].filter(Boolean)
        };
      });
    }

    hasSizeOption(product) {
      return this.optionObjects(product).some(option => isSizeName(option.name));
    }

    hasSelectionOptions(product) {
      return this.optionObjects(product).some(option => (
        isSizeName(option.name) || (isColorName(option.name) && option.values.length > 1)
      ));
    }

    modalProduct(product, selectedVariant) {
      const variants = Array.isArray(product?.variants) ? [...product.variants] : [];
      variants.sort((left, right) => Number(String(right?.id) === String(selectedVariant?.id)) - Number(String(left?.id) === String(selectedVariant?.id)));
      const image = this.imageSources(product, selectedVariant)[0] || '';
      return {
        id: String(product?.id || ''),
        title: String(product?.title || ''),
        vendor: String(product?.vendor || ''),
        image,
        largeImage: image,
        context: 'complete-look',
        options: this.optionObjects(product),
        variants
      };
    }

    openOptionsModal(product, variant, fallbackUrl) {
      const api = window.BHModal;
      if (typeof api?.openProduct !== 'function') {
        window.location.assign(fallbackUrl);
        return;
      }
      const data = this.modalProduct(product, variant);
      if (this.dialog?.open && typeof this.dialog.close === 'function') {
        this.dialog.close();
        confirmationLog('info', 'dialog:paused-for-options', {
          version: VERSION,
          productId: String(product?.id || '')
        });
      }
      api.openProduct(data);
    }

    bagIcon() {
      return '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true"><path d="M9 6.333V4a3 3 0 0 1 6 0v2.333M7.112 6.333h9.777a2.667 2.667 0 0 1 2.656 2.436l.87 10a2.667 2.667 0 0 1-2.657 2.898H6.241a2.667 2.667 0 0 1-2.656-2.898l.87-10a2.667 2.667 0 0 1 2.657-2.436Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    }

    async addVariant(product, variant, sourceColor, button) {
      if (this.adding || !variant?.id || variant.available === false) return;
      const root = window.Shopify?.routes?.root || '/';
      const body = new FormData();
      body.append('id', String(variant.id));
      body.append('quantity', '1');
      this.adding = true;
      button.disabled = true;
      button.classList.add('is-loading');
      button.querySelector('span').textContent = this.dataset.addingLabel;
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
          body
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.description || data.message || this.dataset.addErrorLabel);
        const item = Array.isArray(data.items) ? data.items[0] : data;
        document.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: { item, source: 'bh-cart-confirmation' }
        }));
        const cart = await this.refreshCart();
        button.classList.add('is-added');
        button.querySelector('span').textContent = this.dataset.addedLabel;
        this.addedCount = Math.max(1, (this.addedCount || 1) + 1);
        this.updateAddedTitle();
        await this.loadRecommendations({
          item,
          cart,
          productId: String(item?.product_id || product?.id || ''),
          productTitle: String(item?.product_title || product?.title || ''),
          color: sourceColor,
          source: 'bh-cart-confirmation-refresh'
        });
      } catch (error) {
        button.classList.add('has-error');
        button.querySelector('span').textContent = error?.message || this.dataset.addErrorLabel;
      } finally {
        this.adding = false;
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }

    async refreshCart() {
      try {
        const cart = await this.cartSnapshot();
        ['cart:updated', 'cart:refresh', 'theme:cart:update'].forEach(eventName => {
          document.dispatchEvent(new CustomEvent(eventName, {
            bubbles: true,
            detail: { cart, source: 'bh-cart-confirmation' }
          }));
        });
        document.querySelectorAll('.cart-count, [data-cart-count], [data-bh-cart-count], .cart-count-bubble span[aria-hidden="true"]').forEach(element => {
          element.textContent = cart.item_count;
          element.classList.remove('hidden');
          element.classList.toggle('is-empty', cart.item_count === 0);
          element.closest('.cart-count-bubble')?.classList.remove('hidden');
        });
        window.BHCartUI?.refresh?.();
        return cart;
      } catch (_) {}
    }

    resizeImage(source) {
      try {
        const url = new URL(String(source), window.location.origin);
        url.searchParams.set('width', '600');
        return url.href;
      } catch (_) {
        return String(source || '');
      }
    }

    money(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'es-PE', {
          style: 'currency',
          currency: this.dataset.currency || 'PEN',
          minimumFractionDigits: amount % 1 === 0 ? 0 : 2
        }).format(amount);
      } catch (_) {
        return `${amount.toFixed(2)} ${this.dataset.currency || ''}`.trim();
      }
    }
  }

  customElements.define('bh-cart-confirmation', BHCartConfirmation);
})();
